import {
  ErrorPage,
  FailedIdentificationFinalPage,
  Loader,
  reportErrorToConsole,
  useDownloadVouchedReport,
  useGetCreditApplicationById,
  useGetIdentityVerificationSession,
  VouchedCallbacks,
  VouchedComponent,
  VouchedJob,
} from '@applyonline/shared'
import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { usePrequalify } from '../../api/credit-api'
import { useMerchantLogoWithiFinancePartnership } from '../App/hooks'

const VouchedPage = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [session, isLoadingIdentityVerif] = useGetIdentityVerificationSession(id!, EFinancingProgram.B2c)
  const [creditApp, isLoadingCreditApp] = useGetCreditApplicationById(id!, EFinancingProgram.B2c)
  const [prequalify] = usePrequalify()
  const [downloadVouchedReport] = useDownloadVouchedReport()

  const languageSuffix = i18n.resolvedLanguage === 'fr' ? 'fr' : 'en'
  const logoUrl = useMerchantLogoWithiFinancePartnership(languageSuffix, creditApp)

  const callbacks = useMemo<VouchedCallbacks>(
    () => ({
      onJobValid: (job: VouchedJob) => {
        if (!creditApp) return
        prequalify({ creditApplicationId: creditApp.id, vouchedJobToken: job.token, applicantType: EApplicantType.Applicant })
          .then(() => navigate(`/creditApplication/${creditApp.id}`, { replace: true }))
          .catch(reportErrorToConsole)
      },
      onJobInvalid: (job: VouchedJob) => {
        if (!creditApp) return
        downloadVouchedReport({
          creditApplicationId: creditApp.id,
          vouchedJobToken: job.token,
          financingProgram: EFinancingProgram.B2c,
          applicantType: EApplicantType.Applicant,
        }).catch(reportErrorToConsole)
        navigate(`/creditApplication/${creditApp.id}/failedid`, { replace: true })
      },
      onCameraNoPermission: () => {
        if (!creditApp) return
        navigate(`/creditApplication/${creditApp.id}/cameraPermission`, { replace: true })
      },
    }),
    [creditApp, prequalify, downloadVouchedReport, navigate],
  )

  if (isLoadingIdentityVerif || isLoadingCreditApp)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  if (!creditApp) return <ErrorPage />
  const vouchedFailedCount = creditApp.applicant?.vouchedFailedCount || 0
  if (vouchedFailedCount > 1) return <FailedIdentificationFinalPage creditApp={creditApp} />

  return (
    <VouchedComponent
      session={session}
      creditApplication={creditApp}
      logoUrl={logoUrl}
      callbacks={callbacks}
    />
  )
}

export default VouchedPage
